import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Alert,
  useMediaQuery,
  Slide
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from '@mui/material/styles';
import { getRiskColor, getRiskEmoji } from '../../utils/interactionUtils';
import { r, radius } from '../../theme/tokens';

// Slide up transition for mobile
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * `supplementName` is the modal's TITLE and means different things per caller:
 * the stack's name when opened from a My stacks card, the supplement's name
 * when opened from a recommended card's row chip or the supplements table.
 *
 * That difference is why each row carries its own optional `supplementLabel`
 * rather than the modal deriving one. Opened from a My stacks card the rows
 * span several supplements, so two of them interacting with the same
 * medication render as identical headings and only the reasoning text tells
 * them apart (Aravind, 2026-09-13) — those rows read
 * "<supplement> · <medication>". Opened from either supplement-scoped caller
 * every row already belongs to the supplement in the title, so stamping it on
 * each row would be redundant; those callers pass no label and the heading
 * stays the medication alone.
 */
const InteractionDetailsModal = ({ 
  open, 
  onClose, 
  supplementName, 
  interactions = [],
  primaryInteraction = null 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!open) return null;

  const hasInteractions = interactions.length > 0;
  const interactionCount = interactions.length;

  // Sort interactions by risk priority (High -> Medium -> Low)
  const sortedInteractions = [...interactions].sort((a, b) => {
    const riskPriority = { 'High': 3, 'Medium': 2, 'Low': 1 };
    const aPriority = riskPriority[a.risk] || 0;
    const bPriority = riskPriority[b.risk] || 0;
    return bPriority - aPriority;
  });

  // These twelve literals are deliberately NOT tokens, and Phase Sweep batch 7
  // (STACK-509) left them that way rather than converting them:
  //
  //   1. The severity ramp here duplicates `getRiskColor` in
  //      `utils/interactionUtils.js`, which no sweep batch owns. Tokenising one
  //      half would split one safety palette across two files.
  //   2. No token reproduces these values. `color.destructive` happens to be
  //      the same value as the high-risk red, but the token module forbids
  //      reusing it for interaction risk: sharing the value would exempt the
  //      risk red from `risk-color` app-wide.
  //   3. Mapping onto riskHigh/riskMedium/riskLow would recolour "low" from
  //      amber to green, which changes what the chip tells a user about a drug
  //      interaction. That is a safety change, not a palette move.
  //
  // Converting both files together is a maintainer call; see the PR for 509.
  const getRiskChipColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'high':
        return { backgroundColor: '#ffebee', color: '#d32f2f', borderColor: '#d32f2f' };
      case 'medium':
        return { backgroundColor: '#fff3e0', color: '#f57c00', borderColor: '#f57c00' };
      case 'low':
        return { backgroundColor: '#fffbf0', color: '#ffa000', borderColor: '#ffa000' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666666', borderColor: '#666666' };
    }
  };

  const getOverallRiskLevel = () => {
    if (!hasInteractions) return 'None';
    
    // Simply use the highest risk from any single supplement
    const risks = interactions.map(i => i.risk);
    if (risks.includes('High')) return 'High';
    if (risks.includes('Medium')) return 'Medium';
    if (risks.includes('Low')) return 'Low';
    return 'None';
  };

  const overallRisk = getOverallRiskLevel();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? false : "md"}
      fullWidth={!isMobile}
      fullScreen={false}
      TransitionComponent={isMobile ? SlideTransition : undefined}
      PaperProps={{
        sx: {
          borderRadius: radius.md,
          maxHeight: isMobile ? `calc(100vh - ${r(32)})` : '90vh',
          margin: isMobile ? r(16) : theme.spacing(2),
          width: isMobile ? `calc(100vw - ${r(32)})` : 'auto',
          height: isMobile ? 'auto' : 'auto',
          maxWidth: isMobile ? `calc(100vw - ${r(32)})` : r(600)
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? 0 : theme.spacing(2)
        }
      }}
    >

      <DialogTitle sx={{ pb: isMobile ? 1 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <MedicalServicesIcon sx={{ 
              color: getRiskColor(overallRisk), 
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              flexShrink: 0
            }} />
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              component="div" 
              sx={{ 
                fontWeight: 600,
                fontSize: isMobile ? '1rem' : '1.15rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: isMobile ? 'nowrap' : 'normal',
                lineHeight: 1.3
              }}
            >
              {isMobile ? supplementName.replace(' - Interaction Review', '') : supplementName} - Interaction Review
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'grey.500',
              '&:hover': { backgroundColor: 'grey.100' },
              flexShrink: 0,
              ml: 1
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1 : 2, 
        display: 'flex', 
        flexDirection: 'column', 
        maxHeight: isMobile ? `calc(100vh - ${r(200)})` : '70vh',
        flex: isMobile ? 1 : 'none',
        overflowY: 'hidden'
      }}>
        {hasInteractions ? (
          <>
            {/* Summary Section - Fixed at top */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: isMobile ? 1.5 : 2, 
                mb: isMobile ? 2 : 3, 
                backgroundColor: getRiskColor(overallRisk) + '08',
                border: `${r(1)} solid ${getRiskColor(overallRisk)}40`,
                flexShrink: 0
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: isMobile ? 1 : 2, 
                mb: 1,
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"} 
                  sx={{ 
                    fontWeight: 600, 
                    color: getRiskColor(overallRisk),
                    fontSize: isMobile ? '0.95rem' : '1.1rem',
                    lineHeight: 1.3
                  }}
                >
                  {getRiskEmoji(overallRisk)} {interactionCount} Medication Interaction{interactionCount > 1 ? 's' : ''} Found
                </Typography>
                <Chip 
                  label={`Overall Risk: ${overallRisk}`}
                  variant="outlined"
                  size="small"
                  sx={{
                    ...getRiskChipColor(overallRisk),
                    fontWeight: 600,
                    border: `${r(1)} solid ${getRiskChipColor(overallRisk).borderColor}`,
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    height: isMobile ? r(22) : r(20)
                  }}
                />
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  lineHeight: 1.5,
                  fontSize: isMobile ? '0.85rem' : '0.875rem'
                }}
              >
                This supplement may interact with {interactionCount} of your medication{interactionCount > 1 ? 's' : ''}. 
                Review the details below and consult your healthcare provider before making any changes.
              </Typography>
            </Paper>

            {/* Scrollable Interactions List */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  mb: isMobile ? 1.5 : 1.8, 
                  color: 'text.primary', 
                  flexShrink: 0,
                  fontSize: isMobile ? '1rem' : '1.1rem'
                }}
              >
                Detailed Interaction Analysis
              </Typography>

              <Box sx={{ 
                flexGrow: 1, 
                overflowY: 'auto', 
                pr: isMobile ? 0.5 : 1,
                mr: isMobile ? -0.5 : 0 
              }}>
                <List sx={{ p: 0 }}>
                  {sortedInteractions.map((interaction, index) => (
                    <React.Fragment key={index}>
                      <ListItem 
                        alignItems="flex-start" 
                        sx={{ 
                          px: 0, 
                          py: isMobile ? 1.5 : 2,
                          backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50'
                        }}
                      >
                        <ListItemIcon sx={{ 
                          mt: 0.5, 
                          minWidth: isMobile ? 32 : 40 
                        }}>
                          <Box sx={{ 
                            width: isMobile ? 24 : 28, 
                            height: isMobile ? 24 : 28, 
                            borderRadius: '50%', 
                            backgroundColor: getRiskColor(interaction.risk) + '20',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{ fontSize: isMobile ? r(12) : r(14) }}>
                              {getRiskEmoji(interaction.risk)}
                            </Typography>
                          </Box>
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: isMobile ? 'flex-start' : 'center', 
                              gap: 1, 
                              mb: 1,
                              flexDirection: isMobile ? 'column' : 'row'
                            }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: 'text.primary',
                                  fontSize: isMobile ? '0.95rem' : '0.98rem'
                                }}
                              >
                                {interaction.supplementLabel
                                  ? `${interaction.supplementLabel} · ${interaction.medicationName}`
                                  : interaction.medicationName}
                              </Typography>
                              <Chip 
                                label={`${interaction.risk} Risk`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  ...getRiskChipColor(interaction.risk),
                                  fontSize: isMobile ? '0.7rem' : '0.72rem',
                                  height: isMobile ? r(18) : r(19),
                                  border: `${r(1)} solid ${getRiskChipColor(interaction.risk).borderColor}`
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary', 
                                lineHeight: 1.4,
                                mt: 0.5,
                                fontSize: isMobile ? '0.8rem' : '0.82rem'
                              }}
                            >
                              {interaction.reasoning}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < sortedInteractions.length - 1 && (
                        <Divider variant="inset" component="li" sx={{ ml: 5 }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </Box>

            {/* Safety Notice - Subtle styling for both mobile and desktop */}
            <Box 
              sx={{ 
                mt: isMobile ? 1.5 : 2, 
                p: isMobile ? 1.5 : 2,
                borderRadius: radius.sm,
                backgroundColor: 'rgba(33, 150, 243, 0.03)',
                border: `${r(1)} solid rgba(33, 150, 243, 0.1)`,
                flexShrink: 0
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <InfoIcon sx={{ 
                  fontSize: isMobile ? r(16) : r(18), 
                  color: 'info.main', 
                  mt: 0.1,
                  opacity: 0.7
                }} />
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500, 
                      mb: 0.3,
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                      color: 'text.secondary'
                    }}
                  >
                    Important Safety Information
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{
                      fontSize: isMobile ? '0.65rem' : '0.75rem',
                      lineHeight: 1.3,
                      color: 'text.secondary',
                      opacity: 0.85
                    }}
                  >
                    These interactions are based on current medical literature and AI analysis. Always consult 
                    your healthcare provider or pharmacist before starting, stopping, or changing any supplements, 
                    especially if you're taking prescription medications.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          /* No Interactions State */
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <InfoIcon sx={{ fontSize: '3rem', color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
              No Significant Interactions Found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto' }}>
              Based on current medical literature, {supplementName} does not appear to have any 
              significant interactions with your current medications. However, always consult your 
              healthcare provider before starting any new supplement.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.2 : 1.5, 
        borderTop: `${r(1)} solid`, 
        borderColor: 'rgba(0, 0, 0, 0.06)',
        flexShrink: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.01)'
      }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          fullWidth={isMobile}
          sx={{ 
            borderRadius: radius.sm,
            textTransform: 'none',
            fontWeight: 600,
            minHeight: isMobile ? r(42) : r(40),
            fontSize: isMobile ? '0.9rem' : '0.88rem',
            backgroundColor: 'primary.main',
            minWidth: isMobile ? 'auto' : r(120),
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InteractionDetailsModal;
